'use client';
import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { Building2, Users, DollarSign, Clock, FileText, CheckCircle, Eye, Upload, TrendingUp, Download, Lock, RefreshCw, AlertCircle } from 'lucide-react';
import MetricCard from '@/components/ui/MetricCard';
import StatusBadge from '@/components/ui/StatusBadge';
import SectionHeader from '@/components/ui/SectionHeader';
import { createClient } from '@/lib/supabase/client';

const FundingProgressChart = dynamic(() => import('./FundingProgressChart'), { ssr: false });

interface Campaign {
  id: string;
  campaign_ref: string;
  name: string;
  status: string;
  target_amount: number;
  raised_amount: number;
  investor_count: number;
  avg_investment: number;
  days_remaining: number;
  days_active: number;
  escrow_balance: number;
  platform_fees: number;
  net_proceeds: number;
}

interface WorkflowStage {
  id: string;
  stage_label: string;
  stage_order: number;
  stage_status: string;
}

interface CampaignInvestor {
  id: string;
  investor_name: string;
  investor_type: string;
  amount: number;
  invested_at: string;
  payment_status: string;
  kyc_status: string;
}

interface Milestone {
  id: string;
  label: string;
  target_value: number;
  achieved_value: number;
  status: string;
}

interface VaultDoc {
  id: string;
  name: string;
  category: string;
  access_level: string;
  requires_nda: boolean;
}

export default function IssuerDashboard() {
  const [activeTab, setActiveTab] = useState<'overview' | 'investors' | 'milestones' | 'vault'>('overview');
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [workflowStages, setWorkflowStages] = useState<WorkflowStage[]>([]);
  const [investors, setInvestors] = useState<CampaignInvestor[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [vaultDocs, setVaultDocs] = useState<VaultDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  let supabase = createClient();
  const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch the primary published campaign
      const { data: campData, error: campErr } = await supabase
        .from('campaigns')
        .select('*')
        .eq('status', 'published')
        .order('raised_amount', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (campErr) throw campErr;
      if (campData) {
        setCampaign(campData);

        const [stagesRes, investorsRes, milestonesRes, docsRes] = await Promise.all([
          supabase
            .from('campaign_workflow_stages')
            .select('*')
            .eq('campaign_id', campData.id)
            .order('stage_order', { ascending: true }),
          supabase
            .from('campaign_investors')
            .select('*')
            .eq('campaign_id', campData.id)
            .order('invested_at', { ascending: false })
            .limit(10),
          supabase
            .from('campaign_milestones')
            .select('*')
            .eq('settlement_batch_id', campData.id)
            .limit(10),
          supabase
            .from('vault_documents')
            .select('id, name, category, access_level, requires_nda')
            .order('created_at', { ascending: false })
            .limit(8),
        ]);

        if (stagesRes.data) setWorkflowStages(stagesRes.data);
        if (investorsRes.data) setInvestors(investorsRes.data);
        if (milestonesRes.data) setMilestones(milestonesRes.data);
        if (docsRes.data) setVaultDocs(docsRes.data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load campaign data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ── Real-time data subscriptions ──────────────────────────────────────────
  useEffect(() => {
    let supabase: any = null;
    const subs: any[] = [];
    try {
      supabase = createClient();
      const handler = () => { fetchData(); };

      subs.push(
        supabase
          .channel('issuer-rt-campaigns')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'campaigns' }, handler)
          .subscribe(),
        supabase
          .channel('issuer-rt-workflow-stages')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'campaign_workflow_stages' }, handler)
          .subscribe(),
        supabase
          .channel('issuer-rt-investors')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'campaign_investors' }, handler)
          .subscribe(),
        supabase
          .channel('issuer-rt-docs')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'vault_documents' }, handler)
          .subscribe(),
      );
    } catch {
      // Supabase not configured — subscriptions skipped
    }
    return () => {
      if (supabase) subs.forEach((ch) => supabase.removeChannel(ch));
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw size={24} className="animate-spin text-muted-foreground" />
        <span className="ml-3 text-muted-foreground text-sm">Loading campaign data...</span>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="glass-card rounded-xl p-8 text-center">
        <AlertCircle size={32} className="mx-auto text-muted-foreground mb-3" />
        <p className="text-sm font-medium text-foreground">No active campaigns found</p>
        <p className="text-xs text-muted-foreground mt-1">Create a campaign to get started.</p>
      </div>
    );
  }

  const fundedPct = campaign.target_amount > 0 ? Math.round((campaign.raised_amount / campaign.target_amount) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl font-bold text-foreground tracking-tight">{campaign.name}</h1>
            <StatusBadge variant="published" label="Live" dot />
          </div>
          <p className="text-sm text-muted-foreground">Campaign ID: <span className="font-mono-data text-xs">{campaign.campaign_ref}</span> · {campaign.days_remaining} days remaining</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="btn-secondary text-xs gap-1.5"><RefreshCw size={12} /> Refresh</button>
          <button className="btn-secondary text-xs gap-1.5"><Eye size={12} /> Preview</button>
          <button className="btn-primary text-xs gap-1.5"><Upload size={12} /> Upload Document</button>
        </div>
      </div>

      {error && (
        <div className="alert-banner alert-warning">
          <AlertCircle size={16} className="flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Workflow stage indicator */}
      {workflowStages.length > 0 && (
        <div className="glass-card rounded-xl p-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Campaign Workflow</p>
          <div className="flex items-center gap-0 overflow-x-auto pb-1">
            {workflowStages.map((stage, i) => (
              <React.Fragment key={`stage-${stage.id}`}>
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    stage.stage_status === 'complete' ? 'bg-primary/20 text-primary border border-primary/40' :
                    stage.stage_status === 'current' ? 'bg-primary text-white border border-primary shadow-glow' :
                    'bg-surface-3 text-muted-foreground border border-border'
                  }`}>
                    {stage.stage_status === 'complete' ? <CheckCircle size={14} /> : stage.stage_order}
                  </div>
                  <p className={`text-xs mt-1.5 whitespace-nowrap ${
                    stage.stage_status === 'current' ? 'text-primary font-semibold' :
                    stage.stage_status === 'complete' ? 'text-muted-foreground' : 'text-muted-foreground/50'
                  }`}>{stage.stage_label}</p>
                </div>
                {i < workflowStages.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-1 min-w-4 ${stage.stage_status === 'complete' ? 'bg-primary/40' : 'bg-border'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border overflow-x-auto scrollbar-hide">
        {(['overview', 'investors', 'milestones', 'vault'] as const).map((tab) => (
          <button key={`issuer-tab-${tab}`} onClick={() => setActiveTab(tab)} className={`portal-tab capitalize ${activeTab === tab ? 'active' : ''}`}>
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
            <div className="glass-card rounded-xl p-4 card-hover sm:col-span-2 lg:col-span-1 row-span-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Funding Progress</p>
              <p className="text-3xl font-bold font-mono-data text-foreground">{fmt(campaign.raised_amount)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">of {fmt(campaign.target_amount)} target</p>
              <div className="mt-3 mb-1">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-mono-data font-bold text-primary">{fundedPct}%</span>
                </div>
                <div className="funding-thermometer">
                  <div className="funding-thermometer-fill" style={{ width: `${fundedPct}%` }} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Remaining: <span className="font-mono-data text-foreground">{fmt(campaign.target_amount - campaign.raised_amount)}</span></p>
            </div>
            <MetricCard label="Total Investors" value={String(campaign.investor_count)} subValue={`${campaign.days_active} days active`} trend={22.4} trendLabel="vs prior week" variant="info" icon={Users} iconColor="text-info" />
            <MetricCard label="Avg. Investment Size" value={fmt(campaign.avg_investment)} subValue="Per committed investor" variant="default" icon={TrendingUp} iconColor="text-primary" mono />
            <MetricCard label="Escrow Balance" value={fmt(campaign.escrow_balance)} subValue="Held in regulated escrow" variant="positive" icon={DollarSign} iconColor="text-positive" mono />
            <MetricCard label="Platform Fees" value={fmt(campaign.platform_fees)} subValue="2% of raised capital" variant="default" icon={Building2} iconColor="text-accent" mono />
            <MetricCard label="Days Remaining" value={String(campaign.days_remaining)} subValue="Campaign closing soon" variant={campaign.days_remaining < 14 ? 'warning' : 'default'} icon={Clock} iconColor="text-warning" />
          </div>
          <div className="glass-card rounded-xl p-4">
            <SectionHeader title="Weekly Funding Inflows" subtitle="Capital raised per week since campaign launch" />
            <FundingProgressChart />
          </div>
        </div>
      )}

      {activeTab === 'investors' && (
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <SectionHeader title="Recent Investors" subtitle={`${campaign.investor_count} total committed investors`} />
            <button className="btn-ghost text-xs gap-1.5"><Download size={13} /> Export Register</button>
          </div>
          {investors.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">No investors found for this campaign.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-3">
                  <tr>
                    {['Investor', 'Type', 'Amount', 'Date', 'Payment', 'KYC', 'Actions'].map((h) => (
                      <th key={`th-issr-${h}`} className="table-th">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {investors.map((inv) => (
                    <tr key={inv.id} className="data-table-row">
                      <td className="table-td">
                        <p className="font-medium text-foreground">{inv.investor_name}</p>
                      </td>
                      <td className="table-td">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${inv.investor_type === 'Institutional' ? 'bg-accent/10 text-accent' : 'bg-surface-4 text-muted-foreground'}`}>
                          {inv.investor_type}
                        </span>
                      </td>
                      <td className="table-td font-mono-data font-semibold text-foreground">{fmt(inv.amount)}</td>
                      <td className="table-td font-mono-data text-xs text-muted-foreground">{new Date(inv.invested_at).toLocaleDateString()}</td>
                      <td className="table-td">
                        <StatusBadge variant={inv.payment_status === 'confirmed' ? 'confirmed' : 'pending'} label={inv.payment_status === 'confirmed' ? 'Confirmed' : 'Pending'} dot />
                      </td>
                      <td className="table-td">
                        <StatusBadge variant={inv.kyc_status === 'approved' ? 'kyc-approved' : 'review'} label={inv.kyc_status === 'approved' ? 'Approved' : 'In Review'} />
                      </td>
                      <td className="table-td">
                        <button className="btn-ghost p-1" title="View investor"><Eye size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'milestones' && (
        <div className="glass-card rounded-xl p-4">
          <SectionHeader title="Campaign Milestones" subtitle={`${milestones.length} milestones tracked`} />
          {milestones.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">No milestones configured for this campaign.</div>
          ) : (
            <div className="space-y-3">
              {milestones.map((ms) => {
                const pct = ms.target_value > 0 ? Math.round((ms.achieved_value / ms.target_value) * 100) : 0;
                const isComplete = ms.status === 'met';
                const isPartial = ms.status === 'partial';
                return (
                  <div key={ms.id} className={`rounded-lg p-3 border ${isComplete ? 'border-primary/20 bg-primary/5' : isPartial ? 'border-accent/30 bg-accent/5' : 'border-border bg-surface-3'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${isComplete ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{ms.label}</p>
                        {isPartial && (
                          <div className="mt-2 progress-bar w-48">
                            <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
                          </div>
                        )}
                      </div>
                      <StatusBadge variant={isComplete ? 'settled' : isPartial ? 'review' : 'closed'} label={isComplete ? 'Complete' : isPartial ? 'In Progress' : 'Upcoming'} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'vault' && (
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <SectionHeader title="Document Vault" subtitle={`${vaultDocs.length} documents`} />
            <button className="btn-primary text-xs gap-1.5"><Upload size={13} /> Upload</button>
          </div>
          {vaultDocs.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">No documents in vault.</div>
          ) : (
            <div className="divide-y divide-border">
              {vaultDocs.map((doc) => (
                <div key={doc.id} className="px-4 py-3 flex items-center justify-between hover:bg-surface-3/40 transition-colors">
                  <div className="flex items-center gap-3">
                    <FileText size={16} className="text-muted-foreground flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{doc.name}</p>
                      <p className="text-xs text-muted-foreground">{doc.category} · {doc.access_level}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.requires_nda && <Lock size={13} className="text-warning" />}
                    <button className="btn-ghost p-1.5"><Eye size={13} /></button>
                    <button className="btn-ghost p-1.5"><Download size={13} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}